<script setup lang="ts">
import { joinURL } from 'ufo'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { api } from '~/api/apis'
import { removeAuthCookies } from '~/utils'

const route = useRoute()
const router = useRouter()

const pmID = ref('')
const resourceRight = ref(false)
const subscribeEmail = ref('')
const isActive = ref(false)
const isOn = ref(false)
const preHide = ref(false)

const { accountRole, userRealName, userPicture, canAccessAdmin, isSales } =
  storeToRefs(useUserStore())
const { setUser } = useUserStore()
const { baseURL } = useEnvStore()

const { t, locale } = useI18n()

const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()

const { getUploadFileUrl } = useUrl()

const logoCSS = computed(() => (locale.value === 'tw' ? '' : 'en'))

const localeFBLink = computed(() =>
  locale.value === 'tw' ? 'https://lihi1.com/iwkWu' : 'https://lihi1.com/5GCnS'
)

const wordChange = computed(() =>
  locale.value === 'tw' ? t('nav_mail_word02') : t('nav_mail_word03')
)

async function init() {
  preHide.value = true

  const getProductMainListResult = await useAppApi(
    api.getProductMainList()
  ).call()

  if (!getProductMainListResult.success) {
    return
  }

  const pm = getProductMainListResult.response.data?.find(
    (item) => item.pmCode === 'tpSalesman'
  )

  if (pm) {
    pmID.value = pm.pmID.toString()
    resourceRight.value = accountRole.value.some((role) =>
      pm.role.includes(role)
    )
  }
}

function openMenu() {
  isActive.value = !isActive.value
}

function closeMenu() {
  setTimeout(() => {
    isActive.value = false
  }, 100)
}

function openLangMenu() {
  isOn.value = !isOn.value
}

function closeLangMenu() {
  setTimeout(() => {
    isOn.value = false
  }, 100)
}

function goToAdmin() {
  window.location.href = joinURL(baseURL, '/admin')
}

function onSignOut() {
  removeAuthCookies()
  setUser(null)

  if (route.path !== '/') {
    router.replace({ path: router.resolve('/').path })
  }
}

function onSubscribe() {
  const payload = {
    email: subscribeEmail.value,
    languages: locale.value === 'tw' ? 'TW' : 'EN',
    sourceType: 'portal'
  }

  api.postSubscribe(payload).then(() => {
    $('#subscribeModal').modal('show')
  })
}

function openNav() {
  $('.left-bar').toggleClass('active')
  $('.right-bar').toggleClass('active')
}

function subscribe() {
  $('.subscribe').toggleClass('active')
}

function enterSubscribeShow() {
  $('.subscribe').addClass('active')
}

function leaveSubscribeShow() {
  $('.subscribe').removeClass('active')
}

onMounted(() => {
  init()
})
</script>

<template>
  <nav v-cloak class="navbar navbar-expand-xl navbar-light">
    <nuxt-link
      class="navbar-brand logo"
      :class="logoCSS"
      :to="localePath('index')"
    >
      <img
        v-if="locale === 'tw'"
        class="d-inline-block align-top"
        src="~/assets/app/images/copyright-tw.png"
      />
      <img
        v-else
        class="d-inline-block align-top"
        src="~/assets/app/images/copyright-en.png"
      />
      <span class="logo-word">
        {{ $t('nav_title') }}
      </span>
    </nuxt-link>

    <div class="nav-group">
      <div class="nav-right text-right">
        <!-- RWD button -->
        <button
          aria-controls="main_nav"
          aria-expanded="false"
          aria-label="Toggle navigation"
          class="navbar-toggler"
          data-target="#main_nav"
          data-toggle="collapse"
          type="button"
          @click="openNav"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <!-- tab -->
        <div id="main_nav" class="text-uppercase navbar-collapse collapse">
          <ul class="navbar-nav mt-lg-0 mr-auto mt-2">
            <li class="nav-item">
              <nuxt-link class="nav-link" :to="localePath('question')">
                {{ $t('nav_question') }}
              </nuxt-link>
            </li>
          </ul>
        </div>
      </div>

      <div class="other-info">
        <div class="info-bottom" :class="{ active: isActive }">
          <!-- 登入前會員樣式 - Start -->
          <nuxt-link
            v-if="!userRealName"
            class="outline-btn"
            :to="localePath('sign')"
          >
            <span>
              {{ $t('nav_sign_in') }}
            </span>
          </nuxt-link>
          <!-- 登入前會員樣式 - End -->

          <!-- 登入後會員樣式 - Start -->
          <a
            v-else
            id="dropdownMenuButton"
            class="member"
            href="#"
            @blur="closeMenu"
            @click.prevent="openMenu"
          >
            <span
              v-if="userPicture"
              class="user-picture rounded-circle"
              :style="{
                'background-image': `url(${getUploadFileUrl(userPicture, 'account')})`
              }"
            ></span>
            <span v-else class="user-picture rounded-circle"></span>
            <span class="d-none d-md-inline-block">{{ userRealName }}</span>
            <div class="arrow"></div>
          </a>

          <div class="member-list">
            <nuxt-link
              class="dropdown-item member-link"
              :to="localePath('recording-inquire')"
            >
              {{ $t('nav_recording_inquire') }}
            </nuxt-link>
            <nuxt-link
              class="dropdown-item member-link"
              :to="localePath('registered-edit')"
            >
              {{ $t('nav_registered_edit') }}
            </nuxt-link>
            <a
              v-show="canAccessAdmin"
              class="dropdown-item member-link"
              href="#"
              @click.prevent="goToAdmin"
            >
              {{ $t('nav_admin') }}
            </a>
            <nuxt-link
              v-show="isSales"
              class="dropdown-item member-link"
              :to="localePath({ name: 'product-list', params: { id: pmID } })"
            >
              {{ $t('nav_resource') }}
            </nuxt-link>
            <a
              class="dropdown-item member-link"
              href="#"
              @click.prevent="onSignOut"
            >
              {{ $t('nav_sign_out') }}
            </a>
          </div>
          <!-- 登入後會員樣式 - End -->
        </div>
        <!-- 語系選單 - Start -->
        <div class="info-top">
          <div class="btn-lang">
            <button @blur="closeLangMenu" @click.prevent="openLangMenu">
              <img alt="language" src="~/assets/app/images/icon-lang.png" />
            </button>
            <div class="lang-column" :class="{ on: isOn }">
              <nuxt-link
                class="lang-choose lang-zh-tw"
                :to="switchLocalePath('tw')"
              >
                繁
              </nuxt-link>
              <nuxt-link
                class="lang-choose lang-en"
                :to="switchLocalePath('en')"
              >
                EN
              </nuxt-link>
            </div>
          </div>
        </div>
        <!-- 語系選單 - End -->
      </div>
    </div>

    <!-- left nav -->
    <div class="purple-nav link-left left-bar">
      <nav class="nav flex-column align-items-center">
        <a class="nav-link" target="_blank" :href="localeFBLink">
          <font-awesome :icon="['fab', 'facebook-f']" />
        </a>
        <hr class="hr-down" />
        <a class="nav-link" href="https://lihi1.com/xq6vG" target="_blank">
          <font-awesome :icon="['fab', 'linkedin-in']" />
        </a>
        <hr class="hr-down" />
        <a class="nav-link" href="https://lihi1.com/if1Rj" target="_blank">
          <font-awesome :icon="['fab', 'youtube']" />
        </a>
        <hr class="hr-down" />
        <a
          id="subscribe"
          class="nav-link sub-link cursor-pointer"
          @mouseenter="enterSubscribeShow"
          @mouseleave="leaveSubscribeShow"
        >
          <div class="sub-icon" @click.prevent="subscribe">
            <font-awesome :icon="['far', 'newspaper']" />
          </div>
          <div v-if="preHide" class="subscribe">
            <form @submit.prevent="onSubscribe">
              <span class="sub-word">{{ $t('nav_subscribe') }}</span>
              <div class="sub-input">
                <div class="w-100">
                  <input
                    v-model="subscribeEmail"
                    class="form-control"
                    type="email"
                    required
                    :placeholder="$t('nav_mail_input')"
                  />
                  <button type="submit">
                    <font-awesome :icon="['far', 'paper-plane']" :size="'lg'" />
                  </button>
                </div>
                <div class="privacy w-100">
                  <input type="checkbox" required />
                  <label class="checkbox-container mb-0">
                    {{ $t('nav_mail_word01') }}
                    <span class="text-red">
                      {{ `${wordChange}` }}
                    </span>
                    {{ $t('nav_mail_word04') }}
                    <a data-target="#exampleModalCenter" data-toggle="modal">
                      ...{{ $t('nav_mail_more') }}
                    </a>
                  </label>
                </div>
              </div>
            </form>
          </div>
        </a>
      </nav>
    </div>

    <!-- right nav -->
    <div class="link-right link-left right-bar">
      <div class="right-nav tpi-nav">
        <a href="https://www.tpisoftware.com/" target="_blank">
          {{ $t('nav_tpi') }}
        </a>
      </div>
      <div class="right-nav university-nav">
        <a href="https://www.tpisoftware.com/tpu/" target="_blank">
          {{ $t('nav_tpu') }}
        </a>
      </div>
    </div>
  </nav>
</template>
